import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FiPlus, FiEdit, FiTrash2, FiEye, FiX, FiCheck, 
  FiUpload, FiImage, FiMapPin, FiHome,
  FiStar, FiCamera, FiFileText,
  FiChevronLeft, FiChevronRight, FiSearch,
  FiCalendar, FiTag, FiNavigation,
  FiTrendingUp, FiSettings, FiBarChart,
  FiType, FiGrid, FiLayers, FiAlertTriangle,
  FiDownload, FiRefreshCw
} from 'react-icons/fi';

// Helper function to safely parse JSON
const safeJSONParse = (jsonString, fallback = []) => {
  if (!jsonString || typeof jsonString !== 'string') {
    return fallback;
  }
  
  try {
    // Trim and check if it's a valid JSON string
    const trimmed = jsonString.trim();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
      return fallback;
    }
    
    // Check if it starts and ends with JSON brackets/braces
    const firstChar = trimmed.charAt(0);
    const lastChar = trimmed.charAt(trimmed.length - 1);
    
    if (
      (firstChar === '[' && lastChar === ']') ||
      (firstChar === '{' && lastChar === '}')
    ) {
      return JSON.parse(trimmed);
    }
    
    // If it doesn't look like JSON, return fallback
    return fallback;
  } catch (error) {
    console.error('Error parsing JSON:', error, 'String:', jsonString);
    return fallback;
  }
};

const ProjectManagementDataTable = () => {
  // States
  const [activeTab, setActiveTab] = useState('basic');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [amenities, setAmenities] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    category_id: '',
    project_name: '',
    project_caption: '',
    project_overview: '',
    location: '',
    get_direction: '',
    project_status: '',
    project_process_stage: '',
    amenities: '[]',
    specifications: '[]',
    highlights: '[]',
    nearby_locations: '[]'
  });

  // File states
  const [coverPreview, setCoverPreview] = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [homeImagePreview, setHomeImagePreview] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [homeImageFile, setHomeImageFile] = useState(null);
  const [floorplanFiles, setFloorplanFiles] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [floorplanNames, setFloorplanNames] = useState([]);
  const [galleryNames, setGalleryNames] = useState([]);
  const [highlightInput, setHighlightInput] = useState('');
  const [highlightType, setHighlightType] = useState('text');
  const [currentBatchName, setCurrentBatchName] = useState('Gallery 1');
  const [currentBatchFiles, setCurrentBatchFiles] = useState([]);
  
  // Deletion tracking for backend
  const [deleteFloorplans, setDeleteFloorplans] = useState([]);
  const [deleteGalleryBlocks, setDeleteGalleryBlocks] = useState([]);
  const [deleteGalleryImages, setDeleteGalleryImages] = useState([]);

  // API Configuration
  const API_BASE = 'https://mithahomesfrontend.pemixcels.com/api/mainprojectprocess';
  const API_SAMPLE = 'https://mithahomesfrontend.pemixcels.com/api/mainprojectprocess/projects/sampletes';
  const ImgURL = 'https://mithahomesfrontend.pemixcels.com';
  
  // Status options
  const projectStatuses = [
    { id: 'planning', name: 'Planning', percentage: 10, color: '#f59e0b' },
    { id: 'design', name: 'Design', percentage: 25, color: '#3b82f6' },
    { id: 'construction', name: 'Under Construction', percentage: 50, color: '#8b5cf6' },
    { id: 'finishing', name: 'Finishing', percentage: 75, color: '#10b981' },
    { id: 'ready', name: 'Ready to Move', percentage: 90, color: '#84cc16' },
    { id: 'completed', name: 'Completed', percentage: 100, color: '#06b6d4' }
  ];

  const projectStages = [
    { id: 1, name: 'Pre-launch' },
    { id: 2, name: 'Launch Phase' },
    { id: 3, name: 'Construction Phase' },
    { id: 4, name: 'Handover Phase' },
    { id: 5, name: 'Post-handover' }
  ];

  const highlightTypes = [
    { id: 'text', name: 'Text', icon: <FiType /> }
  ];

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch projects from sample API
      try {
        const projectsRes = await axios.get(API_SAMPLE);
        console.log('API Response:', projectsRes.data);
        
        if (projectsRes.data.success && projectsRes.data.projects) {
          setProjects(projectsRes.data.projects);
          console.log('Projects loaded:', projectsRes.data.projects.length);
        } else {
          toast.error('❌ Unexpected API response structure');
          setProjects([]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('❌ Error loading projects');
        setProjects([]);
      }

      // Fetch categories
      try {
        const catsRes = await axios.get(`https://mithahomesfrontend.pemixcels.com/api/project/all`);
        if (catsRes.data.success) {
          const activeCategories = catsRes.data.data.filter(c => 
            c && (c.status === 'active' || !c.status)
          );
          setCategories(activeCategories);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }

      // Fetch amenities
      try {
        const amenitiesRes = await axios.get(`https://mithahomesfrontend.pemixcels.com/api/amenities/all`);
        if (amenitiesRes.data.success) {
          const activeAmenities = amenitiesRes.data.data.filter(a => 
            a && (a.status === 'active' || !a.status)
          );
          setAmenities(activeAmenities);
        }
      } catch (error) {
        console.error('Error fetching amenities:', error);
      }

      // Fetch specifications
      try {
        const specsRes = await axios.get(`https://mithahomesfrontend.pemixcels.com/api/specification/all`);
        if (specsRes.data.success) {
          const activeSpecs = specsRes.data.data.filter(s => 
            s && (s.status === 'active' || !s.status)
          );
          setSpecifications(activeSpecs);
        }
      } catch (error) {
        console.error('Error fetching specifications:', error);
      }

      // Fetch nearby locations
      try {
        const nearbyRes = await axios.get(`https://mithahomesfrontend.pemixcels.com/api/nearbylocation/all`);
        if (nearbyRes.data.success) {
          const activeLocations = nearbyRes.data.data.filter(n => 
            n && (n.status === 'active' || !n.status)
          );
          setNearbyLocations(activeLocations);
        }
      } catch (error) {
        console.error('Error fetching nearby locations:', error);
      }

    } catch (error) {
      console.error('Error in fetchAllData:', error);
      toast.error('❌ Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh data function
  const refreshData = () => {
    fetchAllData();
    toast.info('🔄 Refreshing data...');
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e, field) => {
    const files = Array.from(e.target.files);
    
    if (field === 'cover_image') {
      const file = files[0];
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    } else if (field === 'banner_image') {
      const file = files[0];
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    } else if (field === 'home_images') {
      const file = files[0];
      setHomeImageFile(file);
      setHomeImagePreview(URL.createObjectURL(file));
    } else if (field === 'floorplans') {
      const newFiles = [...floorplanFiles, ...files];
      setFloorplanFiles(newFiles);
      
      const newNames = [...floorplanNames];
      files.forEach((file, index) => {
        newNames.push({
          id: `fp-${Date.now()}-${index}`,
          name: `Floorplan ${floorplanNames.length + index + 1}`,
          image: file,
          isNew: true,
          isFile: true,
          preview: URL.createObjectURL(file),
          tempName: file.name
        });
      });
      setFloorplanNames(newNames);
    } else if (field === 'gallery_batch') {
      if (files.length > 10) {
        toast.error('❌ Maximum 10 images per batch');
        return;
      }
      setCurrentBatchFiles(files.slice(0, 10));
    }
  };

  const addBatchToGallery = () => {
    if (currentBatchFiles.length === 0) {
      toast.error('❌ No images in current batch');
      return;
    }
    
    const batchName = currentBatchName.trim() || `Gallery ${Math.floor(galleryNames.length / 10) + 1}`;
    const batchId = `gallery-${Date.now()}`;
    
    const newGalleryFiles = [...galleryFiles, ...currentBatchFiles];
    setGalleryFiles(newGalleryFiles);
    
    const newGalleryNames = [...galleryNames];
    currentBatchFiles.forEach((file, index) => {
      newGalleryNames.push({
        id: batchId,
        name: batchName,
        image: file,
        isNew: true,
        isFile: true,
        preview: URL.createObjectURL(file),
        tempName: file.name
      });
    });
    
    setGalleryNames(newGalleryNames);
    setCurrentBatchFiles([]);
    setCurrentBatchName(`Gallery ${Math.floor(newGalleryNames.length / 10) + 2}`);
    toast.success(`✅ ${currentBatchFiles.length} images added to "${batchName}"`);
  };

  // Remove file from floorplans
  const removeFloorplan = (index) => {
    const newNames = [...floorplanNames];
    const floorplanToRemove = newNames[index];
    
    if (floorplanToRemove.isNew && floorplanToRemove.isFile) {
      const fileIndex = floorplanFiles.findIndex(file => file === floorplanToRemove.image);
      if (fileIndex !== -1) {
        const newFiles = [...floorplanFiles];
        newFiles.splice(fileIndex, 1);
        setFloorplanFiles(newFiles);
      }
    } else if (!floorplanToRemove.isNew) {
      setDeleteFloorplans(prev => [...prev, floorplanToRemove.id]);
    }
    
    newNames.splice(index, 1);
    setFloorplanNames(newNames);
    toast.success('✅ Floorplan removed');
  };

  // Remove file from gallery
  const removeGalleryImage = (index) => {
    const newNames = [...galleryNames];
    const galleryItem = newNames[index];
    
    if (galleryItem.isNew && galleryItem.isFile) {
      const fileIndex = galleryFiles.findIndex(file => file === galleryItem.image);
      if (fileIndex !== -1) {
        const newFiles = [...galleryFiles];
        newFiles.splice(fileIndex, 1);
        setGalleryFiles(newFiles);
      }
    } else if (!galleryItem.isNew) {
      const galleryBlock = galleryNames.find(g => g.id === galleryItem.id);
      if (galleryBlock) {
        const imagesInBlock = galleryNames.filter(g => g.id === galleryItem.id);
        
        if (imagesInBlock.length === 1) {
          setDeleteGalleryBlocks(prev => [...prev, galleryItem.id]);
        } else {
          const imageUrl = galleryItem.url || galleryItem.image;
          setDeleteGalleryImages(prev => {
            const existing = prev.find(p => p.gid === galleryItem.id);
            if (existing) {
              return prev.map(p => 
                p.gid === galleryItem.id 
                  ? { ...p, images: [...p.images, imageUrl] }
                  : p
              );
            } else {
              return [...prev, { gid: galleryItem.id, images: [imageUrl] }];
            }
          });
        }
      }
    }
    
    newNames.splice(index, 1);
    setGalleryNames(newNames);
    toast.success('✅ Gallery image removed');
  };

  // Delete gallery group
  const deleteGalleryGroup = (groupName, groupId) => {
    const newGalleryNames = galleryNames.filter(item => item.name !== groupName);
    const removedItems = galleryNames.filter(item => item.name === groupName);
    
    const newGalleryFiles = galleryFiles.filter(file => {
      return !removedItems.some(item => item.isNew && item.image === file);
    });
    
    const existingItems = removedItems.filter(item => !item.isNew);
    if (existingItems.length > 0) {
      setDeleteGalleryBlocks(prev => [...prev, existingItems[0].id]);
    }
    
    setGalleryNames(newGalleryNames);
    setGalleryFiles(newGalleryFiles);
    toast.success(`✅ Gallery group "${groupName}" removed`);
  };

  // FIXED: Checkbox toggle function
  const handleFeatureToggle = (type, id) => {
    setFormData(prev => {
      const currentArray = safeJSONParse(prev[type] || '[]', []);
      let newArray;
      
      const idToCompare = String(id);
      
      if (currentArray.some(item => String(item) === idToCompare)) {
        newArray = currentArray.filter(item => String(item) !== idToCompare);
      } else {
        newArray = [...currentArray, id];
      }
      
      return { ...prev, [type]: JSON.stringify(newArray) };
    });
  };

  const handleAddHighlight = () => {
    if (!highlightInput.trim()) {
      toast.error('❌ Please enter highlight text');
      return;
    }

    const currentHighlights = safeJSONParse(formData.highlights || '[]', []);
    const newHighlight = {
      id: Date.now(),
      text: highlightInput,
      type: highlightType,
      value: highlightInput
    };

    setFormData(prev => ({
      ...prev,
      highlights: JSON.stringify([...currentHighlights, newHighlight])
    }));

    setHighlightInput('');
    toast.success('✅ Highlight added');
  };

  const removeHighlight = (id) => {
    const currentHighlights = safeJSONParse(formData.highlights || '[]', []);
    setFormData(prev => ({
      ...prev,
      highlights: JSON.stringify(currentHighlights.filter(h => h.id !== id))
    }));
  };

  // Update file name
  const updateFileName = (index, type, value) => {
    if (type === 'floorplan') {
      const updated = [...floorplanNames];
      const newName = value.trim() === '' 
        ? `Floorplan ${index + 1}` 
        : value;
      updated[index].name = newName;
      setFloorplanNames(updated);
    }

    if (type === 'gallery') {
      const updated = [...galleryNames];
      const newName = value.trim() === '' 
        ? `Gallery ${Math.floor(index / 10) + 1}` 
        : value;
      
      updated[index].name = newName;
      
      if (!updated[index].isNew) {
        const groupId = updated[index].id;
        updated.forEach((item, idx) => {
          if (item.id === groupId) {
            item.name = newName;
          }
        });
      }
      
      setGalleryNames(updated);
    }
  };

  // FIXED: Edit project - properly load previous selections
  const handleEdit = (project) => {
    console.log('Editing project:', project);
    setEditingProject(project);
    
    // Reset deletion tracking
    setDeleteFloorplans([]);
    setDeleteGalleryBlocks([]);
    setDeleteGalleryImages([]);
    
    // Helper function to parse IDs from database
    const parseIdsFromDB = (fieldName) => {
      try {
        if (!project[fieldName]) return [];
        
        const value = project[fieldName];
        
        // If it's already a stringified array
        if (typeof value === 'string') {
          const parsed = safeJSONParse(value, []);
          
          // Check what type of array we have
          if (parsed.length === 0) return [];
          
          // If it's an array of objects with ID fields
          if (typeof parsed[0] === 'object' && parsed[0] !== null) {
            // Try to extract IDs from common field names
            return parsed.map(item => {
              // Check for various possible ID field names
              if (item.id) return String(item.id);
              if (item.amid) return String(item.amid);
              if (item.sid) return String(item.sid);
              if (item.nid) return String(item.nid);
              if (item.category_id) return String(item.category_id);
              return null;
            }).filter(id => id !== null);
          }
          
          // If it's an array of numbers/strings
          return parsed.map(item => String(item));
        }
        
        // If it's already an array
        if (Array.isArray(value)) {
          if (value.length === 0) return [];
          
          // If it's an array of objects
          if (typeof value[0] === 'object' && value[0] !== null) {
            return value.map(item => {
              if (item.id) return String(item.id);
              if (item.amid) return String(item.amid);
              if (item.sid) return String(item.sid);
              if (item.nid) return String(item.nid);
              if (item.category_id) return String(item.category_id);
              return null;
            }).filter(id => id !== null);
          }
          
          // If it's an array of numbers/strings
          return value.map(item => String(item));
        }
        
        return [];
      } catch (error) {
        console.error(`Error parsing ${fieldName}:`, error);
        return [];
      }
    };
    
    // Parse highlights separately (they should be array of objects)
    const parseHighlightsFromDB = () => {
      try {
        if (!project.highlights) return [];
        
        if (typeof project.highlights === 'string') {
          return safeJSONParse(project.highlights, []);
        }
        
        if (Array.isArray(project.highlights)) {
          return project.highlights;
        }
        
        return [];
      } catch (error) {
        console.error('Error parsing highlights:', error);
        return [];
      }
    };
    
    // Get data from database
    const amenitiesData = parseIdsFromDB('amenities');
    const specificationsData = parseIdsFromDB('specifications');
    const highlightsData = parseHighlightsFromDB();
    const nearbyLocationsData = parseIdsFromDB('nearby_locations');
    
    console.log('Parsed data for editing:', {
      amenities: amenitiesData,
      specifications: specificationsData,
      highlights: highlightsData,
      nearby_locations: nearbyLocationsData
    });
    
    // Set form data with stringified arrays
    setFormData({
      category_id: project.category_id ? String(project.category_id) : '',
      project_name: project.project_name || '',
      project_caption: project.project_caption || '',
      project_overview: project.project_overview || '',
      location: project.location || '',
      get_direction: project.get_direction || '',
      project_status: project.project_status || '',
      project_process_stage: project.project_process_stage || '',
      amenities: JSON.stringify(amenitiesData),
      specifications: JSON.stringify(specificationsData),
      highlights: JSON.stringify(highlightsData),
      nearby_locations: JSON.stringify(nearbyLocationsData)
    });
    
    // Set image previews
    const setImagePreview = (imageField, setPreview) => {
      if (project[imageField]) {
        const fullUrl = project[imageField].startsWith('http') 
          ? project[imageField] 
          : `${ImgURL}${project[imageField]}`;
        setPreview(fullUrl);
      } else {
        setPreview('');
      }
    };
    
    setImagePreview('cover_image', setCoverPreview);
    setImagePreview('banner_image', setBannerPreview);
    setImagePreview('home_image', setHomeImagePreview);
    
    // Parse floorplans
    const parsedFloorplans = [];
    try {
      let floorplans = [];
      
      if (project.floorplans) {
        if (typeof project.floorplans === 'string') {
          floorplans = safeJSONParse(project.floorplans, []);
        } else if (Array.isArray(project.floorplans)) {
          floorplans = project.floorplans;
        }
      }
      
      console.log('Raw floorplans:', floorplans);
      
      floorplans.forEach((fp, index) => {
        if (!fp) return;
        
        const name = fp.name || `Floorplan ${index + 1}`;
        const imagePath = fp.image || '';
        const fullUrl = imagePath.startsWith('http') 
          ? imagePath 
          : imagePath ? `${ImgURL}${imagePath}` : '';
        
        parsedFloorplans.push({
          id: fp.id || `fp-${Date.now()}-${index}`,
          name: name,
          image: imagePath,
          isNew: false,
          isFile: false,
          url: fullUrl,
          preview: fullUrl
        });
      });
    } catch (error) {
      console.error('Error parsing floorplans:', error);
    }
    
    // Parse gallery
    const parsedGallery = [];
    try {
      let galleryData = [];
      
      if (project.gallery) {
        if (typeof project.gallery === 'string') {
          galleryData = safeJSONParse(project.gallery, []);
        } else if (Array.isArray(project.gallery)) {
          galleryData = project.gallery;
        }
      }
      
      console.log('Raw gallery data:', galleryData);
      
      galleryData.forEach((galleryGroup, groupIndex) => {
        if (!galleryGroup) return;
        
        const groupName = galleryGroup.name || `Gallery ${groupIndex + 1}`;
        const groupId = galleryGroup.id || `gallery-${Date.now()}-${groupIndex}`;
        
        const images = galleryGroup.images || [];
        images.forEach((image, imgIndex) => {
          const imagePath = typeof image === 'string' ? image : (image?.url || image);
          if (!imagePath) return;
          
          const fullUrl = imagePath.startsWith('http') 
            ? imagePath 
            : imagePath ? `${ImgURL}${imagePath}` : '';
          
          parsedGallery.push({
            id: groupId,
            name: groupName,
            image: imagePath,
            isNew: false,
            isFile: false,
            url: fullUrl,
            preview: fullUrl
          });
        });
      });
    } catch (error) {
      console.error('Error parsing gallery:', error);
    }

    console.log('Final floorplans:', parsedFloorplans);
    console.log('Final gallery items:', parsedGallery);

    setFloorplanNames(parsedFloorplans);
    setGalleryNames(parsedGallery);
    
    // Reset file states
    setFloorplanFiles([]);
    setGalleryFiles([]);
    setCoverFile(null);
    setBannerFile(null);
    setHomeImageFile(null);
    setCurrentBatchName('Gallery 1');
    setCurrentBatchFiles([]);

    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      category_id: '',
      project_name: '',
      project_caption: '',
      project_overview: '',
      location: '',
      get_direction: '',
      project_status: '',
      project_process_stage: '',
      amenities: '[]',
      specifications: '[]',
      highlights: '[]',
      nearby_locations: '[]'
    });
    setCoverPreview('');
    setBannerPreview('');
    setHomeImagePreview('');
    setCoverFile(null);
    setBannerFile(null);
    setHomeImageFile(null);
    setFloorplanFiles([]);
    setGalleryFiles([]);
    setFloorplanNames([]);
    setGalleryNames([]);
    setHighlightInput('');
    setHighlightType('text');
    setCurrentBatchName('Gallery 1');
    setCurrentBatchFiles([]);
    setEditingProject(null);
    setDeleteFloorplans([]);
    setDeleteGalleryBlocks([]);
    setDeleteGalleryImages([]);
  };

  // Validate form
  const validateForm = () => {
    if (!formData.category_id) {
      toast.error('❌ Please select a category');
      return false;
    }
    if (!formData.project_name?.trim()) {
      toast.error('❌ Please enter project name');
      return false;
    }
    if (!formData.location?.trim()) {
      toast.error('❌ Please enter location');
      return false;
    }
    if (!formData.project_status) {
      toast.error('❌ Please select project status');
      return false;
    }
    
    const totalGalleryImages = getRemainingImagesCount();
    
    // For new projects, require at least 10 images
    if (!editingProject && totalGalleryImages < 10) {
      toast.error(`❌ Please add at least 10 gallery images (currently: ${totalGalleryImages})`);
      return false;
    }
    
    // For editing projects, if they delete too many images, require new ones
    if (editingProject && totalGalleryImages < 10) {
      toast.error(`❌ Need ${10 - totalGalleryImages} more images to reach minimum 10 gallery images`);
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();
      
      // Add all form data
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images if they exist
      if (coverFile) formDataToSend.append('cover_image', coverFile);
      if (bannerFile) formDataToSend.append('banner_image', bannerFile);
      if (homeImageFile) formDataToSend.append('home_images', homeImageFile);
      
      // Prepare floorplan data
      const floorplanData = [];
      const newFloorplanFiles = [];
      
      floorplanNames.forEach((fp, index) => {
        if (deleteFloorplans.includes(fp.id)) {
          return;
        }
        
        const floorplanItem = {
          id: fp.id || `fp-${Date.now()}-${index}`,
          name: fp.name || `Floorplan ${index + 1}`
        };
        
        if (fp.isNew && fp.image instanceof File) {
          floorplanItem.tempName = fp.image.name;
          newFloorplanFiles.push(fp.image);
        }
        
        floorplanData.push(floorplanItem);
      });
      
      if (floorplanData.length > 0) {
        formDataToSend.append('floorplan_names', JSON.stringify(floorplanData));
      }
      
      newFloorplanFiles.forEach(file => {
        formDataToSend.append('floorplans', file);
      });
      
      if (deleteFloorplans.length > 0) {
        formDataToSend.append('delete_floorplans', JSON.stringify(deleteFloorplans));
      }

      // Prepare gallery data
      const galleryGroups = {};
      const newGalleryFiles = [];
      
      galleryNames.forEach((galleryItem) => {
        if (deleteGalleryBlocks.includes(galleryItem.id)) {
          return;
        }
        
        const isMarkedForDeletion = !galleryItem.isNew && 
          deleteGalleryImages.some(d => d.gid === galleryItem.id && d.images.includes(galleryItem.url));
        
        if (isMarkedForDeletion) {
          return;
        }
        
        const groupId = galleryItem.id;
        const groupName = galleryItem.name || `Gallery ${Math.floor(galleryNames.indexOf(galleryItem) / 10) + 1}`;
        
        if (!galleryGroups[groupId]) {
          galleryGroups[groupId] = {
            id: groupId,
            name: groupName,
            total_images: 0
          };
        }
        
        galleryGroups[groupId].total_images++;
        
        if (galleryItem.isNew && galleryItem.image instanceof File) {
          galleryItem.tempName = galleryItem.image.name;
          newGalleryFiles.push(galleryItem.image);
        }
      });
      
      const galleryData = Object.values(galleryGroups);
      
      if (galleryData.length > 0) {
        formDataToSend.append('gallery_names', JSON.stringify(galleryData));
      }
      
      newGalleryFiles.forEach(file => {
        formDataToSend.append('gallery', file);
      });
      
      if (deleteGalleryBlocks.length > 0) {
        formDataToSend.append('delete_gallery', JSON.stringify(deleteGalleryBlocks));
      }
      
      if (deleteGalleryImages.length > 0) {
        formDataToSend.append('delete_gallery_images', JSON.stringify(deleteGalleryImages));
      }

      let response;
      const apiUrl = editingProject 
        ? `${API_BASE}/edit/project/${editingProject.pid || editingProject.id}`
        : `${API_BASE}/add/project`;

      console.log('Sending request to:', apiUrl);
      
      response = await axios({
        method: editingProject ? 'put' : 'post',
        url: apiUrl,
        data: formDataToSend,
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        },
        timeout: 60000
      });

      if (response.data.success) {
        toast.success(`✅ Project ${editingProject ? 'updated' : 'added'} successfully!`);
        setShowModal(false);
        resetForm();
        fetchAllData();
      } else {
        toast.error(`❌ ${response.data.error || response.data.message || 'Error saving project'}`);
      }

    } catch (error) {
      console.error('Error saving project:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Error saving project';
      toast.error(`❌ ${errorMsg}`);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  // Delete project
  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/project/${id}`);
      
      if (response.data.success) {
        toast.success('🗑️ Project deleted successfully!');
        setShowDeleteModal(false);
        setProjectToDelete(null);
        fetchAllData();
      } else {
        toast.error(`❌ ${response.data.error || response.data.message || 'Error deleting project'}`);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('❌ Error deleting project. Please try again.');
    }
  };

  // View project
  const handleView = (project) => {
    setViewingProject(project);
    setShowViewModal(true);
  };

  // Helper functions
  const getStatusConfig = (status) => {
    const statusObj = projectStatuses.find(s => s.id === status) || projectStatuses[0];
    return {
      name: statusObj.name,
      color: statusObj.color,
      percentage: statusObj.percentage,
      background: `${statusObj.color}20`
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.project_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Statistics
  const stats = {
    total: projects.length,
    planning: projects.filter(p => p.project_status === 'planning').length,
    construction: projects.filter(p => p.project_status === 'construction').length,
    completed: projects.filter(p => p.project_status === 'completed').length
  };

  // FIXED: Get parsed arrays for display
  const getParsedArray = (jsonString) => {
    try {
      const parsed = safeJSONParse(jsonString, []);
      return parsed.map(item => String(item));
    } catch (error) {
      console.error('Error in getParsedArray:', error);
      return [];
    }
  };

  // FIXED: Get parsed highlights array
  const getParsedHighlights = () => {
    try {
      return safeJSONParse(formData.highlights || '[]', []);
    } catch (error) {
      console.error('Error in getParsedHighlights:', error);
      return [];
    }
  };

  // FIXED: Check if an item is selected
  const isItemSelected = (type, itemId) => {
    try {
      const jsonString = formData[type] || '[]';
      const parsedArray = safeJSONParse(jsonString, []);
      
      // Convert all to strings for comparison
      return parsedArray.some(id => String(id) === String(itemId));
    } catch (error) {
      console.error('Error in isItemSelected:', error);
      return false;
    }
  };

  // Get gallery groups for display
  const getGalleryGroups = () => {
    const groups = {};
    galleryNames.forEach(item => {
      const groupKey = item.id;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: item.name,
          id: item.id,
          items: []
        };
      }
      groups[groupKey].items.push(item);
    });
    return Object.values(groups);
  };

  // Calculate total gallery images
  const getTotalGalleryImages = () => {
    return galleryNames.length;
  };

  // Calculate remaining images after deletions
  const getRemainingImagesCount = () => {
    const remainingExistingImages = galleryNames.filter(item => {
      if (item.isNew) return false;
      
      if (deleteGalleryBlocks.includes(item.id)) return false;
      
      const isMarkedForDeletion = deleteGalleryImages.some(d => 
        d.gid === item.id && d.images.includes(item.url)
      );
      
      return !isMarkedForDeletion;
    }).length;
    
    const newImagesCount = galleryNames.filter(item => item.isNew).length;
    
    return remainingExistingImages + newImagesCount;
  };

  // Check if an existing image is marked for deletion
  const isImageMarkedForDeletion = (galleryItem) => {
    if (galleryItem.isNew) return false;
    
    if (deleteGalleryBlocks.includes(galleryItem.id)) return true;
    
    return deleteGalleryImages.some(d => 
      d.gid === galleryItem.id && d.images.includes(galleryItem.url)
    );
  };

  // Check if an existing floorplan is marked for deletion
  const isFloorplanMarkedForDeletion = (floorplan) => {
    if (floorplan.isNew) return false;
    return deleteFloorplans.includes(floorplan.id);
  };

  return (
    <div style={{ 
      padding: '20px', 
      minHeight: '100vh', 
      background: '#f8fafc',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        flexWrap: 'wrap', 
        gap: '20px' 
      }}>
        <div>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            color: '#1e293b', 
            margin: 0, 
            lineHeight: '1.2',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Project Management
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#64748b', 
            margin: '8px 0 0' 
          }}>
            Manage all your construction and real estate projects
          </p>
        </div>
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          alignItems: 'center' 
        }}>
          <button 
            onClick={refreshData}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              padding: '12px 20px', 
              background: '#f1f5f9', 
              color: '#64748b', 
              border: 'none', 
              borderRadius: '12px', 
              fontSize: '16px', 
              fontWeight: '500', 
              cursor: 'pointer', 
              transition: 'all 0.3s ease', 
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              ':hover': {
                background: '#e2e8f0',
                transform: 'translateY(-2px)'
              }
            }}
            title="Refresh Data"
          >
            <FiRefreshCw /> Refresh
          </button>
          <button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              padding: '12px 24px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '12px', 
              fontSize: '16px', 
              fontWeight: '600', 
              cursor: 'pointer', 
              transition: 'all 0.3s ease', 
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              ':hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
              }
            }}
          >
            <FiPlus style={{ fontSize: '18px' }} />
            Add New Project
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '24px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
          transition: 'transform 0.3s ease',
          ':hover': {
            transform: 'translateY(-5px)'
          }
        }}>
          <div style={{ 
            width: '56px', 
            height: '56px', 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontSize: '24px',
            background: 'linear-gradient(135deg, #667eea, #667eea99)'
          }}>
            <FiHome />
          </div>
          <div>
            <h3 style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: 0, 
              lineHeight: '1' 
            }}>{stats.total}</h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: '8px 0 0', 
              fontWeight: '500' 
            }}>Total Projects</p>
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '24px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
          transition: 'transform 0.3s ease',
          ':hover': {
            transform: 'translateY(-5px)'
          }
        }}>
          <div style={{ 
            width: '56px', 
            height: '56px', 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontSize: '24px',
            background: 'linear-gradient(135deg, #f59e0b, #f59e0b99)'
          }}>
            <FiSettings />
          </div>
          <div>
            <h3 style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: 0, 
              lineHeight: '1' 
            }}>{stats.planning}</h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: '8px 0 0', 
              fontWeight: '500' 
            }}>Planning</p>
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '24px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
          transition: 'transform 0.3s ease',
          ':hover': {
            transform: 'translateY(-5px)'
          }
        }}>
          <div style={{ 
            width: '56px', 
            height: '56px', 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontSize: '24px',
            background: 'linear-gradient(135deg, #8b5cf6, #8b5cf699)'
          }}>
            <FiBarChart />
          </div>
          <div>
            <h3 style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: 0, 
              lineHeight: '1' 
            }}>{stats.construction}</h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: '8px 0 0', 
              fontWeight: '500' 
            }}>Under Construction</p>
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '24px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 
          transition: 'transform 0.3s ease',
          ':hover': {
            transform: 'translateY(-5px)'
          }
        }}>
          <div style={{ 
            width: '56px', 
            height: '56px', 
            borderRadius: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontSize: '24px',
            background: 'linear-gradient(135deg, #10b981, #10b98199)'
          }}>
            <FiCheck />
          </div>
          <div>
            <h3 style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: 0, 
              lineHeight: '1' 
            }}>{stats.completed}</h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: '8px 0 0', 
              fontWeight: '500' 
            }}>Completed</p>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        flexWrap: 'wrap', 
        gap: '20px' 
      }}>
        <div style={{ 
          flex: 1, 
          minWidth: '300px', 
          position: 'relative' 
        }}>
          <FiSearch style={{ 
            position: 'absolute', 
            left: '16px', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#94a3b8', 
            fontSize: '20px' 
          }} />
          <input
            type="text"
            placeholder="Search projects by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '14px 20px 14px 52px', 
              border: '2px solid #e2e8f0', 
              borderRadius: '12px', 
              fontSize: '16px', 
              transition: 'all 0.3s ease', 
              background: 'white',
              ':focus': {
                outline: 'none',
                borderColor: '#667eea',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
              }
            }}
          />
        </div>
        
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          alignItems: 'center', 
          flexWrap: 'wrap' 
        }}>
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ 
              padding: '12px 16px', 
              border: '2px solid #e2e8f0', 
              borderRadius: '12px', 
              fontSize: '16px', 
              background: 'white', 
              color: '#1e293b', 
              cursor: 'pointer', 
              minWidth: '180px',
              ':focus': {
                outline: 'none',
                borderColor: '#667eea'
              }
            }}
          >
            <option value="all">All Status</option>
            {projectStatuses.map(status => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          
          <button 
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
            }}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              padding: '12px 20px', 
              background: '#f1f5f9', 
              color: '#64748b', 
              border: 'none', 
              borderRadius: '12px', 
              fontSize: '16px', 
              fontWeight: '500', 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              ':hover': {
                background: '#e2e8f0'
              }
            }}
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Projects Table */}
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        overflow: 'hidden', 
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '30px'
      }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '60px', 
            textAlign: 'center' 
          }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              border: '3px solid #e2e8f0', 
              borderTopColor: '#667eea', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite', 
              marginBottom: '20px' 
            }}></div>
            <p>Loading projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '80px 40px', 
            textAlign: 'center' 
          }}>
            <FiHome style={{ 
              fontSize: '64px', 
              color: '#cbd5e1', 
              marginBottom: '20px' 
            }} />
            <h3>No projects found</h3>
            <p>Try adjusting your search or add a new project</p>
            <button 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                padding: '12px 24px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                fontSize: '16px', 
                fontWeight: '600', 
                cursor: 'pointer', 
                transition: 'all 0.3s ease',
                marginTop: '20px'
              }}
            >
              <FiPlus /> Add Your First Project
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              minWidth: '800px' 
            }}>
              <thead>
                <tr>
                  <th style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    textAlign: 'left', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>Project Details</th>
                  <th style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    textAlign: 'left', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>Location</th>
                  <th style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    textAlign: 'left', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>Status & Progress</th>
                  <th style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    textAlign: 'left', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>Created Date</th>
                  <th style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    textAlign: 'left', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => {
                  const statusConfig = getStatusConfig(project.project_status);
                  const category = categories.find(c => c.id == project.category_id);
                  
                  return (
                    <tr key={project.pid} style={{ ':hover': { background: '#f8fafc' } }}>
                      <td style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: '16px' 
                        }}>
                          {project.cover_image ? (
                            <img 
                              src={project.cover_image.startsWith('http') ? project.cover_image : `${ImgURL}${project.cover_image}`}
                              alt={project.project_name}
                              style={{ 
                                width: '80px', 
                                height: '60px', 
                                borderRadius: '8px', 
                                objectFit: 'cover', 
                                flexShrink: 0 
                              }}
                            />
                          ) : (
                            <div style={{ 
                              width: '80px', 
                              height: '60px', 
                              borderRadius: '8px', 
                              background: '#f1f5f9', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              color: '#94a3b8', 
                              flexShrink: 0 
                            }}>
                              <FiImage />
                            </div>
                          )}
                          <div>
                            <h4 
                              style={{ 
                                fontSize: '18px', 
                                fontWeight: '600', 
                                color: '#1e293b', 
                                margin: '0 0 6px', 
                                cursor: 'pointer', 
                                transition: 'color 0.3s ease',
                                ':hover': {
                                  color: '#667eea'
                                }
                              }}
                              onClick={() => handleView(project)}
                            >
                              {project.project_name}
                            </h4>
                            <p style={{ 
                              fontSize: '14px', 
                              color: '#64748b', 
                              margin: '0 0 12px', 
                              lineHeight: '1.4' 
                            }}>
                              {project.project_caption || 'No caption'}
                            </p>
                            <div style={{ 
                              display: 'flex', 
                              gap: '16px', 
                              fontSize: '12px', 
                              color: '#94a3b8' 
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FiCalendar /> {formatDate(project.created_at)}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FiTag /> {category?.name || 'Unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: '12px' 
                        }}>
                          <FiMapPin style={{ 
                            color: '#ef4444', 
                            fontSize: '18px', 
                            marginTop: '2px', 
                            flexShrink: 0 
                          }} />
                          <div>
                            <p style={{ 
                              fontSize: '16px', 
                              color: '#1e293b', 
                              margin: '0 0 8px', 
                              lineHeight: '1.4' 
                            }}>
                              {project.location}
                            </p>
                            {project.get_direction && (
                              <a 
                                href={project.get_direction}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  fontSize: '14px', 
                                  color: '#667eea', 
                                  textDecoration: 'none', 
                                  transition: 'color 0.3s ease',
                                  ':hover': {
                                    color: '#764ba2'
                                  }
                                }}
                              >
                                <FiNavigation /> Get Directions
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      <td style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <div>
                          <div 
                            style={{
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              padding: '6px 12px', 
                              borderRadius: '20px', 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              marginBottom: '12px',
                              background: `${statusConfig.color}20`,
                              color: statusConfig.color,
                              border: `1px solid ${statusConfig.color}40`
                            }}
                          >
                            <FiTrendingUp />
                            {statusConfig.name}
                          </div>
                          <div style={{ width: '100%' }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginBottom: '6px', 
                              fontSize: '14px', 
                              color: '#64748b' 
                            }}>
                              <span>Progress</span>
                              <span style={{ color: statusConfig.color }}>
                                {statusConfig.percentage}%
                              </span>
                            </div>
                            <div style={{ 
                              height: '8px', 
                              background: '#e2e8f0', 
                              borderRadius: '4px', 
                              overflow: 'hidden' 
                            }}>
                              <div 
                                style={{
                                  height: '100%', 
                                  borderRadius: '4px', 
                                  transition: 'width 0.3s ease',
                                  width: `${statusConfig.percentage}%`,
                                  background: statusConfig.color
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '8px', 
                          fontSize: '14px', 
                          color: '#64748b' 
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiCalendar />
                            {formatDate(project.created_at)}
                          </div>
                        </div>
                      </td>
                      
                      <td style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '8px' 
                        }}>
                          <button
                            onClick={() => handleView(project)}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '8px 16px', 
                              border: 'none', 
                              borderRadius: '8px', 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              cursor: 'pointer', 
                              transition: 'all 0.3s ease', 
                              width: '100%', 
                              justifyContent: 'center',
                              ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                              },
                              background: '#f0f9ff', 
                              color: '#0ea5e9',
                              ':hover': {
                                background: '#e0f2fe'
                              }
                            }}
                            title="View Details"
                          >
                            <FiEye /> View
                          </button>
                          <button
                            onClick={() => handleEdit(project)}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '8px 16px', 
                              border: 'none', 
                              borderRadius: '8px', 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              cursor: 'pointer', 
                              transition: 'all 0.3s ease', 
                              width: '100%', 
                              justifyContent: 'center',
                              ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                              },
                              background: '#fef3c7', 
                              color: '#d97706',
                              ':hover': {
                                background: '#fde68a'
                              }
                            }}
                            title="Edit"
                          >
                            <FiEdit /> Edit
                          </button>
                          <button
                            onClick={() => showDeleteConfirmation(project)}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '8px 16px', 
                              border: 'none', 
                              borderRadius: '8px', 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              cursor: 'pointer', 
                              transition: 'all 0.3s ease', 
                              width: '100%', 
                              justifyContent: 'center',
                              ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                              },
                              background: '#fee2e2', 
                              color: '#dc2626',
                              ':hover': {
                                background: '#fecaca'
                              }
                            }}
                            title="Delete"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && projectToDelete && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000, 
          padding: '20px', 
          backdropFilter: 'blur(4px)' 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '20px', 
            width: '100%', 
            maxWidth: '500px', 
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '24px 32px', 
              borderBottom: '1px solid #e2e8f0',
              background: '#fee2e2'
            }}>
              <FiAlertTriangle style={{ 
                fontSize: '24px', 
                color: '#dc2626' 
              }} />
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#dc2626', 
                margin: 0 
              }}>Confirm Deletion</h2>
            </div>
            
            <div style={{ padding: '32px' }}>
              <p style={{ 
                fontSize: '16px', 
                color: '#374151', 
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete the project:
              </p>
              <p style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: '18px',
                margin: '8px 0 16px'
              }}>"{projectToDelete.project_name}"</p>
              <p style={{ fontSize: '16px', color: '#dc2626', fontWeight: '500', marginTop: '16px' }}>
                ⚠️ This action cannot be undone. All project data including images will be permanently deleted.
              </p>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px' 
              }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setProjectToDelete(null);
                  }}
                  style={{ 
                    padding: '12px 24px', 
                    background: '#f1f5f9', 
                    color: '#64748b', 
                    border: 'none', 
                    borderRadius: '12px', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    ':hover': {
                      background: '#e2e8f0'
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(projectToDelete.pid)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    padding: '12px 24px', 
                    background: '#dc2626', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    ':hover': {
                      background: '#b91c1c',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                    }
                  }}
                >
                  <FiTrash2 /> Delete Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Project Modal */}
      {showViewModal && viewingProject && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000, 
          padding: '20px', 
          backdropFilter: 'blur(4px)' 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '20px', 
            width: '100%', 
            maxWidth: '800px', 
            maxHeight: '90vh', 
            overflowY: 'auto', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '24px 32px', 
              borderBottom: '1px solid #e2e8f0' 
            }}>
              <h2>Project Details</h2>
              <button 
                onClick={() => setShowViewModal(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '32px', 
                  color: '#94a3b8', 
                  cursor: 'pointer', 
                  padding: 0, 
                  width: '32px', 
                  height: '32px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  borderRadius: '50%', 
                  transition: 'all 0.3s ease',
                  ':hover': {
                    background: '#f1f5f9',
                    color: '#64748b'
                  }
                }}
              >
                &times;
              </button>
            </div>
            
            <div style={{ padding: '32px' }}>
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                {viewingProject.cover_image && (
                  <img 
                    src={viewingProject.cover_image.startsWith('http') ? viewingProject.cover_image : `${ImgURL}${viewingProject.cover_image}`}
                    alt={viewingProject.project_name}
                    style={{ width: '150px', height: '100px', borderRadius: '8px', objectFit: 'cover' }}
                  />
                )}
                <div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '24px', color: '#1e293b' }}>
                    {viewingProject.project_name}
                  </h3>
                  <p style={{ margin: '0 0 12px', color: '#64748b' }}>
                    {viewingProject.project_caption}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', color: '#94a3b8', fontSize: '14px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FiMapPin /> {viewingProject.location}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FiCalendar /> {formatDate(viewingProject.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '18px', margin: '0 0 12px', color: '#1e293b' }}>Project Overview</h4>
                <p style={{ color: '#64748b', lineHeight: '1.6' }}>
                  {viewingProject.project_overview || 'No overview provided.'}
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingProject);
                  }}
                  style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    padding: '8px 16px', 
                    border: 'none', 
                    borderRadius: '8px', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease', 
                    background: '#fef3c7', 
                    color: '#d97706',
                    ':hover': {
                      background: '#fde68a'
                    }
                  }}
                >
                  <FiEdit /> Edit Project
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    padding: '12px 32px', 
                    background: '#f1f5f9', 
                    color: '#64748b', 
                    border: 'none', 
                    borderRadius: '12px', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    cursor: 'pointer', 
                    transition: 'all 0.3s ease',
                    ':hover': {
                      background: '#e2e8f0'
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Project Modal */}
      {showModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000, 
          padding: '20px', 
          backdropFilter: 'blur(4px)' 
        }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '20px', 
            width: '100%', 
            maxWidth: '900px', 
            maxHeight: '90vh', 
            overflowY: 'auto', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '24px 32px', 
              borderBottom: '1px solid #e2e8f0' 
            }}>
              <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
              <button 
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '32px', 
                  color: '#94a3b8', 
                  cursor: 'pointer', 
                  padding: 0, 
                  width: '32px', 
                  height: '32px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  borderRadius: '50%', 
                  transition: 'all 0.3s ease',
                  ':hover': {
                    background: '#f1f5f9',
                    color: '#64748b'
                  }
                }}
              >
                &times;
              </button>
            </div>

            {/* Tabs */}
            <div style={{ 
              display: 'flex', 
              padding: '0 32px', 
              borderBottom: '1px solid #e2e8f0', 
              background: '#f8fafc' 
            }}>
              {['basic', 'features', 'media'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '16px 24px', 
                    background: 'none', 
                    border: 'none', 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    color: '#64748b', 
                    cursor: 'pointer', 
                    position: 'relative', 
                    transition: 'all 0.3s ease',
                    ':hover': {
                      color: '#667eea'
                    },
                    ...(activeTab === tab ? { 
                      color: '#667eea', 
                      fontWeight: '600',
                      ':after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-1px',
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: '#667eea',
                        borderRadius: '3px 3px 0 0'
                      }
                    } : {})
                  }}
                >
                  {tab === 'basic' ? 'Basic Info' : 
                   tab === 'features' ? 'Features & Amenities' : 
                   'Media & Gallery'}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
              <div style={{ minHeight: '400px' }}>
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                    gap: '20px' 
                  }}>
                    {/* Category */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Category *
                      </label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name || cat.category_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Name */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Project Name *
                      </label>
                      <input
                        type="text"
                        name="project_name"
                        value={formData.project_name}
                        onChange={handleInputChange}
                        placeholder="Enter project name"
                        required
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </div>

                    {/* Project Caption */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px',
                      gridColumn: '1 / -1'
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Project Caption
                      </label>
                      <input
                        type="text"
                        name="project_caption"
                        value={formData.project_caption}
                        onChange={handleInputChange}
                        placeholder="Brief project description"
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </div>

                    {/* Location */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Location *
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Enter project location"
                        required
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </div>

                    {/* Get Direction URL */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Google Maps URL
                      </label>
                      <input
                        type="url"
                        name="get_direction"
                        value={formData.get_direction}
                        onChange={handleInputChange}
                        placeholder="https://maps.google.com/..."
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </div>

                    {/* Project Status */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Project Status *
                      </label>
                      <select
                        name="project_status"
                        value={formData.project_status}
                        onChange={handleInputChange}
                        required
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      >
                        <option value="">Select Status</option>
                        {projectStatuses.map(status => (
                          <option key={status.id} value={status.id}>
                            {status.name} ({status.percentage}%)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Stage */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Project Stage
                      </label>
                      <select
                        name="project_process_stage"
                        value={formData.project_process_stage}
                        onChange={handleInputChange}
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      >
                        <option value="">Select Stage</option>
                        {projectStages.map(stage => (
                          <option key={stage.id} value={stage.name}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Overview */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px',
                      gridColumn: '1 / -1'
                    }}>
                      <label style={{
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151'
                      }}>
                        Project Overview
                      </label>
                      <textarea
                        name="project_overview"
                        value={formData.project_overview}
                        onChange={handleInputChange}
                        placeholder="Detailed project description..."
                        rows="4"
                        style={{ 
                          padding: '12px 16px', 
                          border: '2px solid #e2e8f0', 
                          borderRadius: '12px', 
                          fontSize: '16px', 
                          transition: 'all 0.3s ease', 
                          background: 'white', 
                          width: '100%', 
                          resize: 'vertical', 
                          minHeight: '100px',
                          ':focus': {
                            outline: 'none',
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Features & Amenities Tab */}
                {activeTab === 'features' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '30px' 
                  }}>
                    {/* Amenities - FIXED */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Select Amenities
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                          (Selected: {getParsedArray(formData.amenities).length})
                        </span>
                      </label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: '12px' 
                      }}>
                        {amenities.map(amenity => {
                          const isChecked = isItemSelected('amenities', amenity.amid);
                          return (
                            <label 
                              key={amenity.amid} 
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px', 
                                padding: '12px 16px', 
                                border: '2px solid #e2e8f0', 
                                borderRadius: '12px', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                background: 'white',
                                ':hover': {
                                  borderColor: '#cbd5e1'
                                },
                                ...(isChecked ? { 
                                  borderColor: '#667eea', 
                                  background: '#f0f5ff' 
                                } : {})
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleFeatureToggle('amenities', amenity.amid)}
                                style={{ margin: 0 }}
                              />
                              <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                                {amenity.amenities_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Specifications - FIXED */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Select Specifications
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                          (Selected: {getParsedArray(formData.specifications).length})
                        </span>
                      </label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: '12px' 
                      }}>
                        {specifications.map(spec => {
                          const isChecked = isItemSelected('specifications', spec.sid);
                          return (
                            <label 
                              key={spec.sid} 
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px', 
                                padding: '12px 16px', 
                                border: '2px solid #e2e8f0', 
                                borderRadius: '12px', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                background: 'white',
                                ':hover': {
                                  borderColor: '#cbd5e1'
                                },
                                ...(isChecked ? { 
                                  borderColor: '#667eea', 
                                  background: '#f0f5ff' 
                                } : {})
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleFeatureToggle('specifications', spec.sid)}
                                style={{ margin: 0 }}
                              />
                              <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                                {spec.specification_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Nearby Locations - FIXED */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Select Nearby Locations
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                          (Selected: {getParsedArray(formData.nearby_locations).length})
                        </span>
                      </label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: '12px' 
                      }}>
                        {nearbyLocations.map(location => {
                          const isChecked = isItemSelected('nearby_locations', location.nid);
                          return (
                            <label 
                              key={location.nid} 
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px', 
                                padding: '12px 16px', 
                                border: '2px solid #e2e8f0', 
                                borderRadius: '12px', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                background: 'white',
                                ':hover': {
                                  borderColor: '#cbd5e1'
                                },
                                ...(isChecked ? { 
                                  borderColor: '#667eea', 
                                  background: '#f0f5ff' 
                                } : {})
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleFeatureToggle('nearby_locations', location.nid)}
                                style={{ margin: 0 }}
                              />
                              <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                                {location.nearby_location_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Project Highlights - FIXED */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Project Highlights
                        <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                          ({getParsedHighlights().length} highlights)
                        </span>
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '16px' 
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          flexWrap: 'wrap' 
                        }}>
                          {highlightTypes.map(type => (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => setHighlightType(type.id)}
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '10px 16px', 
                                border: '2px solid #e2e8f0', 
                                borderRadius: '12px', 
                                background: 'white', 
                                color: '#64748b', 
                                fontSize: '14px', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease',
                                ':hover': {
                                  borderColor: '#cbd5e1'
                                },
                                ...(highlightType === type.id ? { 
                                  borderColor: '#667eea', 
                                  background: '#f0f5ff', 
                                  color: '#667eea' 
                                } : {})
                              }}
                            >
                              {type.icon}
                              {type.name}
                            </button>
                          ))}
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px' 
                        }}>
                          <input
                            type={highlightType === 'number' ? 'number' : 'text'}
                            value={highlightInput}
                            onChange={(e) => setHighlightInput(e.target.value)}
                            placeholder={
                              highlightType === 'text' ? 'Enter highlight text...' :
                              highlightType === 'number' ? 'Enter number...' :
                              'Enter percentage...'
                            }
                            style={{ 
                              flex: 1, 
                              padding: '12px 16px', 
                              border: '2px solid #e2e8f0', 
                              borderRadius: '12px', 
                              fontSize: '16px', 
                              transition: 'all 0.3s ease',
                              ':focus': {
                                outline: 'none',
                                borderColor: '#667eea',
                                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleAddHighlight}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '12px 24px', 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '12px', 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              cursor: 'pointer', 
                              transition: 'all 0.3s ease',
                              ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                              }
                            }}
                          >
                            <FiPlus /> Add
                          </button>
                        </div>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px', 
                        minHeight: '60px' 
                      }}>
                        {getParsedHighlights().map((highlight) => (
                          <div key={highlight.id} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            padding: '12px 16px', 
                            background: '#f8fafc', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0' 
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '12px' 
                            }}>
                              <FiStar style={{ 
                                color: '#f59e0b', 
                                fontSize: '18px' 
                              }} />
                              <span style={{ 
                                fontSize: '16px', 
                                color: '#1e293b' 
                              }}>
                                {highlight.value || highlight.text}
                                {highlight.type === 'percentage' && '%'}
                              </span>
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#e2e8f0', 
                                color: '#64748b', 
                                borderRadius: '6px', 
                                fontSize: '12px', 
                                fontWeight: '500' 
                              }}>
                                {highlight.type}
                              </span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              gap: '8px' 
                            }}>
                              <button
                                type="button"
                                onClick={() => removeHighlight(highlight.id)}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  width: '32px', 
                                  height: '32px', 
                                  border: 'none', 
                                  background: '#fee2e2', 
                                  color: '#dc2626', 
                                  borderRadius: '8px', 
                                  cursor: 'pointer', 
                                  transition: 'all 0.3s ease',
                                  ':hover': {
                                    background: '#fecaca'
                                  }
                                }}
                              >
                                <FiX />
                              </button>
                            </div>
                          </div>
                        ))}
                        {getParsedHighlights().length === 0 && (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '24px', 
                            color: '#94a3b8', 
                            fontSize: '14px', 
                            background: '#f8fafc', 
                            borderRadius: '12px', 
                            border: '2px dashed #e2e8f0' 
                          }}>
                            No highlights added yet. Add text, numbers, or percentages.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Media & Gallery Tab */}
                {activeTab === 'media' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '30px' 
                  }}>
                    {/* Cover Image */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Cover Image *
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '24px', 
                        flexWrap: 'wrap' 
                      }}>
                        {coverPreview ? (
                          <div style={{ 
                            position: 'relative', 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px' 
                          }}>
                            <img
                              src={coverPreview}
                              alt="Cover preview"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '16px', 
                                objectFit: 'cover' 
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setCoverPreview('');
                                setCoverFile(null);
                              }}
                              style={{ 
                                position: 'absolute', 
                                top: '-8px', 
                                right: '-8px', 
                                width: '32px', 
                                height: '32px', 
                                border: 'none', 
                                background: '#dc2626', 
                                color: 'white', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                fontSize: '18px',
                                ':hover': {
                                  background: '#b91c1c',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label style={{ 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px', 
                            border: '3px dashed #cbd5e1', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '12px', 
                            cursor: 'pointer', 
                            transition: 'all 0.3s ease', 
                            background: '#f8fafc',
                            ':hover': {
                              borderColor: '#94a3b8',
                              background: '#f1f5f9'
                            }
                          }}>
                            <FiUpload style={{ 
                              fontSize: '32px', 
                              color: '#94a3b8' 
                            }} />
                            <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                              Upload Cover
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'cover_image')}
                              style={{ display: 'none' }}
                            />
                          </label>
                        )}
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: '14px' }}>
                            Main project image (Recommended: 1200×800)
                          </p>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                            This will be displayed as the main project thumbnail
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Banner Image */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Banner Image
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '24px', 
                        flexWrap: 'wrap' 
                      }}>
                        {bannerPreview ? (
                          <div style={{ 
                            position: 'relative', 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px' 
                          }}>
                            <img
                              src={bannerPreview}
                              alt="Banner preview"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '16px', 
                                objectFit: 'cover' 
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setBannerPreview('');
                                setBannerFile(null);
                              }}
                              style={{ 
                                position: 'absolute', 
                                top: '-8px', 
                                right: '-8px', 
                                width: '32px', 
                                height: '32px', 
                                border: 'none', 
                                background: '#dc2626', 
                                color: 'white', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                fontSize: '18px',
                                ':hover': {
                                  background: '#b91c1c',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label style={{ 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px', 
                            border: '3px dashed #cbd5e1', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '12px', 
                            cursor: 'pointer', 
                            transition: 'all 0.3s ease', 
                            background: '#f8fafc',
                            ':hover': {
                              borderColor: '#94a3b8',
                              background: '#f1f5f9'
                            }
                          }}>
                            <FiUpload style={{ 
                              fontSize: '32px', 
                              color: '#94a3b8' 
                            }} />
                            <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                              Upload Banner
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'banner_image')}
                              style={{ display: 'none' }}
                            />
                          </label>
                        )}
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: '14px' }}>
                            Project banner image (Recommended: 1920×400)
                          </p>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                            Displayed at the top of project details page
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Home Card Image */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '16px',
                        display: 'block'
                      }}>
                        Home Card Image *
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '24px', 
                        flexWrap: 'wrap' 
                      }}>
                        {homeImagePreview ? (
                          <div style={{ 
                            position: 'relative', 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px' 
                          }}>
                            <img
                              src={homeImagePreview}
                              alt="Home card preview"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '16px', 
                                objectFit: 'cover' 
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setHomeImagePreview('');
                                setHomeImageFile(null);
                              }}
                              style={{ 
                                position: 'absolute', 
                                top: '-8px', 
                                right: '-8px', 
                                width: '32px', 
                                height: '32px', 
                                border: 'none', 
                                background: '#dc2626', 
                                color: 'white', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease', 
                                fontSize: '18px',
                                ':hover': {
                                  background: '#b91c1c',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label style={{ 
                            flexShrink: 0, 
                            width: '200px', 
                            height: '150px', 
                            border: '3px dashed #cbd5e1', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '12px', 
                            cursor: 'pointer', 
                            transition: 'all 0.3s ease', 
                            background: '#f8fafc',
                            ':hover': {
                              borderColor: '#94a3b8',
                              background: '#f1f5f9'
                            }
                          }}>
                            <FiUpload style={{ 
                              fontSize: '32px', 
                              color: '#94a3b8' 
                            }} />
                            <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                              Upload Home Card
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'home_images')}
                              style={{ display: 'none' }}
                            />
                          </label>
                        )}
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: '14px' }}>
                            Home page card image (Recommended: 400×300)
                          </p>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                            This will be displayed on home page project cards
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Floorplans */}
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: '16px' 
                      }}>
                        <label style={{
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#1e293b',
                          marginBottom: '16px',
                          display: 'block'
                        }}>
                          Floorplans
                          {editingProject && (
                            <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                              (Click on existing floorplans to remove them)
                            </span>
                          )}
                        </label>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          padding: '10px 20px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                          color: 'white', 
                          borderRadius: '12px', 
                          fontSize: '14px', 
                          fontWeight: '500', 
                          cursor: 'pointer', 
                          transition: 'all 0.3s ease',
                          ':hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                          }
                        }}>
                          <FiPlus /> Add Floorplans
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handleFileUpload(e, 'floorplans')}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      
                      {floorplanNames.length > 0 ? (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                          gap: '20px' 
                        }}>
                          {floorplanNames.map((floorplan, index) => {
                            const displayName = (floorplan.name && floorplan.name.trim() !== "") 
                              ? floorplan.name 
                              : `Floorplan ${index + 1}`;
                            const isMarkedForDeletion = isFloorplanMarkedForDeletion(floorplan);
                            
                            return (
                              <div 
                                key={index} 
                                style={{
                                  position: 'relative', 
                                  border: '2px solid #e2e8f0', 
                                  borderRadius: '12px', 
                                  overflow: 'hidden', 
                                  background: 'white',
                                  transition: 'all 0.3s ease',
                                  ':hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                                  },
                                  borderColor: isMarkedForDeletion ? '#dc2626' : '#e2e8f0',
                                  opacity: isMarkedForDeletion ? 0.6 : 1
                                }}
                              >
                                <img
                                  src={floorplan.preview || floorplan.url}
                                  alt={displayName}
                                  style={{ 
                                    width: '100%', 
                                    height: '120px', 
                                    objectFit: 'cover' 
                                  }}
                                />
                                <div style={{ padding: '12px' }}>
                                  <input
                                    type="text"
                                    value={floorplan.name || ''}
                                    onChange={(e) => updateFileName(index, 'floorplan', e.target.value)}
                                    placeholder={`Floorplan ${index + 1}`}
                                    style={{
                                      width: '100%', 
                                      padding: '8px 12px', 
                                      border: '1px solid #e2e8f0', 
                                      borderRadius: '8px', 
                                      fontSize: '14px',
                                      ':focus': {
                                        outline: 'none',
                                        borderColor: '#667eea'
                                      }
                                    }}
                                    disabled={isMarkedForDeletion}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFloorplan(index)}
                                  style={{ 
                                    position: 'absolute', 
                                    top: '8px', 
                                    right: '8px', 
                                    width: '28px', 
                                    height: '28px', 
                                    border: 'none', 
                                    background: 'rgba(220, 38, 38, 0.9)', 
                                    color: 'white', 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    cursor: 'pointer', 
                                    transition: 'all 0.3s ease', 
                                    fontSize: '16px',
                                    ':hover': {
                                      background: '#b91c1c',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                  title={isMarkedForDeletion ? "Restore floorplan" : "Remove floorplan"}
                                >
                                  {isMarkedForDeletion ? <FiCheck /> : <FiX />}
                                </button>
                                {!floorplan.isNew && !isMarkedForDeletion && (
                                  <div style={{ 
                                    position: 'absolute', 
                                    top: '8px', 
                                    left: '8px', 
                                    background: 'rgba(16, 185, 129, 0.9)', 
                                    color: 'white', 
                                    padding: '4px 8px', 
                                    borderRadius: '6px', 
                                    fontSize: '12px', 
                                    fontWeight: '500' 
                                  }}>
                                    Existing
                                  </div>
                                )}
                                {isMarkedForDeletion && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    background: 'rgba(220, 38, 38, 0.9)',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                  }}>
                                    To be deleted
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          padding: '40px', 
                          background: '#f8fafc', 
                          borderRadius: '16px', 
                          border: '2px dashed #e2e8f0', 
                          textAlign: 'center', 
                          gap: '12px' 
                        }}>
                          <FiFileText style={{ 
                            fontSize: '48px', 
                            color: '#cbd5e1' 
                          }} />
                          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                            No floorplans uploaded yet
                          </p>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                            Upload floorplan images with descriptive names
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Gallery Images */}
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: '16px' 
                      }}>
                        <label style={{
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#1e293b',
                          marginBottom: '16px',
                          display: 'block'
                        }}>
                          Gallery Images {!editingProject && '(Minimum 10 required)'}
                        </label>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: '500' 
                        }}>
                          <span style={getRemainingImagesCount() >= 10 ? { 
                            color: '#10b981' 
                          } : { 
                            color: '#ef4444' 
                          }}>
                            {getRemainingImagesCount()} images remaining
                            {editingProject && (deleteGalleryBlocks.length > 0 || deleteGalleryImages.length > 0) && (
                              <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                                ({deleteGalleryBlocks.length} blocks, {deleteGalleryImages.length} images marked for deletion)
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Batch Upload */}
                      <div style={{ 
                        background: '#f8fafc', 
                        borderRadius: '16px', 
                        padding: '24px', 
                        border: '2px solid #e2e8f0' 
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '20px', 
                          flexWrap: 'wrap', 
                          gap: '16px' 
                        }}>
                          <h4 style={{ fontSize: '18px', color: '#1e293b', margin: 0 }}>
                            Batch Upload (Max 10 images per batch)
                          </h4>
                          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                            Current batch: {currentBatchFiles.length} images
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '16px' 
                        }}>
                          <input
                            type="text"
                            value={currentBatchName}
                            onChange={(e) => setCurrentBatchName(e.target.value)}
                            placeholder="Enter batch name (e.g., Exterior, Interior)"
                            style={{ 
                              padding: '12px 16px', 
                              border: '2px solid #cbd5e1', 
                              borderRadius: '12px', 
                              fontSize: '16px', 
                              transition: 'all 0.3s ease',
                              ':focus': {
                                outline: 'none',
                                borderColor: '#667eea',
                                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                              }
                            }}
                          />
                          
                          <div style={{ 
                            display: 'flex', 
                            gap: '12px', 
                            flexWrap: 'wrap' 
                          }}>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '12px 24px', 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                              color: 'white', 
                              borderRadius: '12px', 
                              fontSize: '14px', 
                              fontWeight: '500', 
                              cursor: 'pointer', 
                              transition: 'all 0.3s ease',
                              ':hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                              }
                            }}>
                              <FiUpload /> Select Images
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleFileUpload(e, 'gallery_batch')}
                                style={{ display: 'none' }}
                              />
                            </label>
                            
                            <button
                              type="button"
                              onClick={addBatchToGallery}
                              disabled={currentBatchFiles.length === 0}
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '12px 24px', 
                                background: '#10b981', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '12px', 
                                fontSize: '14px', 
                                fontWeight: '500', 
                                cursor: 'pointer', 
                                transition: 'all 0.3s ease',
                                ':hover': {
                                  background: '#059669',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                                },
                                ...(currentBatchFiles.length === 0 ? { 
                                  opacity: '0.5', 
                                  cursor: 'not-allowed',
                                  ':hover': {
                                    transform: 'none',
                                    boxShadow: 'none'
                                  }
                                } : {})
                              }}
                            >
                              <FiPlus /> Add to Gallery
                            </button>
                          </div>
                        </div>

                        {/* Current Batch Preview */}
                        {currentBatchFiles.length > 0 && (
                          <div style={{ 
                            marginTop: '24px', 
                            background: 'white', 
                            borderRadius: '12px', 
                            padding: '16px', 
                            border: '2px solid #e2e8f0' 
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginBottom: '16px' 
                            }}>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                                Current Batch Preview ({currentBatchFiles.length} images)
                              </span>
                              <button
                                type="button"
                                onClick={() => setCurrentBatchFiles([])}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  padding: '6px 12px', 
                                  background: '#fee2e2', 
                                  color: '#dc2626', 
                                  border: 'none', 
                                  borderRadius: '8px', 
                                  fontSize: '14px', 
                                  cursor: 'pointer', 
                                  transition: 'all 0.3s ease',
                                  ':hover': {
                                    background: '#fecaca'
                                  }
                                }}
                              >
                                <FiX /> Clear
                              </button>
                            </div>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                              gap: '12px' 
                            }}>
                              {currentBatchFiles.map((file, index) => (
                                <div key={index} style={{ position: 'relative' }}>
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Batch ${index + 1}`}
                                    style={{ 
                                      width: '100%', 
                                      height: '80px', 
                                      borderRadius: '8px', 
                                      objectFit: 'cover' 
                                    }}
                                  />
                                  <span style={{ 
                                    position: 'absolute', 
                                    bottom: '4px', 
                                    right: '4px', 
                                    background: 'rgba(0, 0, 0, 0.7)', 
                                    color: 'white', 
                                    fontSize: '12px', 
                                    padding: '2px 6px', 
                                    borderRadius: '4px' 
                                  }}>{index + 1}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Gallery Groups */}
                      {galleryNames.length > 0 ? (
                        <div style={{ marginTop: '30px' }}>
                          <h4 style={{ fontSize: '18px', color: '#1e293b', marginBottom: '20px' }}>
                            Gallery Groups ({getGalleryGroups().length} groups, {getTotalGalleryImages()} total images)
                          </h4>
                          
                          {getGalleryGroups().map((group, groupIndex) => {
                            const isBlockMarkedForDeletion = deleteGalleryBlocks.includes(group.id);
                            const imagesInBlockMarkedForDeletion = deleteGalleryImages.find(d => d.gid === group.id);
                            
                            return (
                              <div 
                                key={group.id} 
                                style={{
                                  marginBottom: '30px', 
                                  border: '2px solid #e2e8f0', 
                                  borderRadius: '16px', 
                                  overflow: 'hidden',
                                  transition: 'all 0.3s ease',
                                  ':hover': {
                                    borderColor: '#cbd5e1',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                  },
                                  borderColor: isBlockMarkedForDeletion ? '#dc2626' : '#e2e8f0',
                                  opacity: isBlockMarkedForDeletion ? 0.6 : 1
                                }}
                              >
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center', 
                                  padding: '16px 20px', 
                                  background: '#f8fafc', 
                                  borderBottom: '2px solid #e2e8f0' 
                                }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px' 
                                  }}>
                                    <span style={{ 
                                      fontSize: '18px', 
                                      fontWeight: '600', 
                                      color: '#1e293b' 
                                    }}>{group.name}</span>
                                    <span style={{
                                      background: '#e2e8f0', 
                                      color: '#64748b', 
                                      padding: '4px 12px', 
                                      borderRadius: '20px', 
                                      fontSize: '14px', 
                                      fontWeight: '500' 
                                    }}>
                                      {group.items.length} images
                                      {isBlockMarkedForDeletion && ' (marked for deletion)'}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => deleteGalleryGroup(group.name, group.id)}
                                    style={{
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px', 
                                      padding: '8px 16px', 
                                      background: '#fee2e2', 
                                      color: '#dc2626', 
                                      border: 'none', 
                                      borderRadius: '8px', 
                                      fontSize: '14px', 
                                      fontWeight: '500', 
                                      cursor: 'pointer', 
                                      transition: 'all 0.3s ease',
                                      ':hover': {
                                        background: '#fecaca',
                                        transform: 'translateY(-2px)'
                                      }
                                    }}
                                    title={isBlockMarkedForDeletion ? "Restore gallery block" : "Delete gallery block"}
                                  >
                                    <FiTrash2 /> {isBlockMarkedForDeletion ? 'Restore Block' : 'Delete Block'}
                                  </button>
                                </div>
                                
                                <div style={{ 
                                  padding: '20px', 
                                  display: 'grid', 
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                                  gap: '20px' 
                                }}>
                                  {group.items.map((item, itemIndex) => {
                                    const isMarkedForDeletion = isImageMarkedForDeletion(item);
                                    
                                    return (
                                      <div 
                                        key={itemIndex} 
                                        style={{
                                          border: '1px solid #e2e8f0', 
                                          borderRadius: '12px', 
                                          overflow: 'hidden', 
                                          background: 'white',
                                          transition: 'all 0.3s ease',
                                          position: 'relative',
                                          ':hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                                          },
                                          borderColor: isMarkedForDeletion ? '#dc2626' : '#e2e8f0',
                                          opacity: isMarkedForDeletion ? 0.6 : 1
                                        }}
                                      >
                                        <div style={{ position: 'relative' }}>
                                          <img
                                            src={item.preview || item.url}
                                            alt={`${group.name} ${itemIndex + 1}`}
                                            style={{ 
                                              width: '100%', 
                                              height: '150px', 
                                              objectFit: 'cover' 
                                            }}
                                          />
                                          <div style={{ 
                                            position: 'absolute', 
                                            bottom: 0, 
                                            left: 0, 
                                            right: 0, 
                                            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))', 
                                            padding: '12px', 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center' 
                                          }}>
                                            <span style={{ 
                                              color: 'white', 
                                              fontSize: '12px', 
                                              fontWeight: '500' 
                                            }}>
                                              Image {itemIndex + 1}
                                            </span>
                                            <span style={{ 
                                              color: 'white', 
                                              fontSize: '12px', 
                                              background: 'rgba(0, 0, 0, 0.6)', 
                                              padding: '2px 8px', 
                                              borderRadius: '10px' 
                                            }}>
                                              #{groupIndex + 1}-{itemIndex + 1}
                                            </span>
                                          </div>
                                        </div>
                                        <div style={{ 
                                          padding: '12px', 
                                          display: 'flex', 
                                          gap: '8px', 
                                          alignItems: 'center' 
                                        }}>
                                          <input
                                            type="text"
                                            value={item.name || ''}
                                            onChange={(e) => {
                                              const newGalleryNames = [...galleryNames];
                                              const itemIndex = newGalleryNames.findIndex(g => 
                                                g.id === item.id && g.image === item.image
                                              );
                                              if (itemIndex !== -1) {
                                                updateFileName(itemIndex, 'gallery', e.target.value);
                                              }
                                            }}
                                            placeholder="Gallery Name"
                                            style={{
                                              flex: 1, 
                                              padding: '8px 12px', 
                                              border: '1px solid #e2e8f0', 
                                              borderRadius: '8px', 
                                              fontSize: '14px',
                                              ':focus': {
                                                outline: 'none',
                                                borderColor: '#667eea'
                                              },
                                              background: item.isNew ? 'white' : '#f8fafc',
                                              opacity: isMarkedForDeletion ? 0.6 : 1
                                            }}
                                            disabled={isMarkedForDeletion}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => removeGalleryImage(itemIndex)}
                                            style={{
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              justifyContent: 'center', 
                                              width: '36px', 
                                              height: '36px', 
                                              border: 'none', 
                                              background: '#fee2e2', 
                                              color: '#dc2626', 
                                              borderRadius: '8px', 
                                              cursor: 'pointer', 
                                              transition: 'all 0.3s ease', 
                                              fontSize: '16px',
                                              ':hover': {
                                                background: '#fecaca',
                                                transform: 'scale(1.1)'
                                              }
                                            }}
                                            title={isMarkedForDeletion ? "Restore image" : "Remove image"}
                                          >
                                            {isMarkedForDeletion ? <FiCheck /> : <FiTrash2 />}
                                          </button>
                                        </div>
                                        {!item.isNew && !isMarkedForDeletion && (
                                          <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            left: '8px',
                                            background: 'rgba(16, 185, 129, 0.9)',
                                            color: 'white',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                          }}>
                                            Existing
                                          </div>
                                        )}
                                        {isMarkedForDeletion && (
                                          <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            left: '8px',
                                            background: 'rgba(220, 38, 38, 0.9)',
                                            color: 'white',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                          }}>
                                            To be deleted
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          
                          {getRemainingImagesCount() < 10 && (
                            <div style={{
                              background: '#fee2e2',
                              color: '#dc2626',
                              padding: '16px',
                              borderRadius: '12px',
                              marginTop: '20px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <FiAlertTriangle /> Need {10 - getRemainingImagesCount()} more images to reach minimum 10 gallery images
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          gap: '20px', 
                          padding: '40px', 
                          background: '#f8fafc', 
                          borderRadius: '16px', 
                          border: '2px dashed #e2e8f0', 
                          textAlign: 'center' 
                        }}>
                          <FiImage style={{ 
                            fontSize: '48px', 
                            color: '#cbd5e1', 
                            flexShrink: 0 
                          }} />
                          <div>
                            <h4 style={{ fontSize: '18px', color: '#1e293b', margin: '0 0 8px' }}>
                              No gallery images yet
                            </h4>
                            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: '14px' }}>
                              Upload images in batches of up to 10 images each
                            </p>
                            {!editingProject && (
                              <p style={{ color: '#ef4444', fontWeight: '500' }}>
                                ⚠️ Minimum 10 images required for new projects
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                paddingTop: '32px', 
                borderTop: '1px solid #e2e8f0', 
                marginTop: '32px' 
              }}>
                <div>
                  {activeTab !== 'basic' && (
                    <button
                      type="button"
                      onClick={() => {
                        const tabs = ['basic', 'features', 'media'];
                        const currentIndex = tabs.indexOf(activeTab);
                        setActiveTab(tabs[currentIndex - 1]);
                      }}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        padding: '12px 24px', 
                        background: '#f1f5f9', 
                        color: '#64748b', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontSize: '16px', 
                        fontWeight: '500', 
                        cursor: 'pointer', 
                        transition: 'all 0.3s ease',
                        ':hover': {
                          background: '#e2e8f0'
                        }
                      }}
                    >
                      <FiChevronLeft /> Previous
                    </button>
                  )}
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '16px' 
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    style={{
                      padding: '12px 32px', 
                      background: '#f1f5f9', 
                      color: '#64748b', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '16px', 
                      fontWeight: '500', 
                      cursor: 'pointer', 
                      transition: 'all 0.3s ease',
                      ':hover': {
                        background: '#e2e8f0'
                      }
                    }}
                  >
                    Cancel
                  </button>

                  {activeTab !== 'media' ? (
                    <button
                      type="button"
                      onClick={() => {
                        const tabs = ['basic', 'features', 'media'];
                        const currentIndex = tabs.indexOf(activeTab);
                        setActiveTab(tabs[currentIndex + 1]);
                      }}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        padding: '12px 32px', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontSize: '16px', 
                        fontWeight: '500', 
                        cursor: 'pointer', 
                        transition: 'all 0.3s ease',
                        ':hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }
                      }}
                    >
                      Next <FiChevronRight />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      style={{
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        padding: '12px 32px', 
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontSize: '16px', 
                        fontWeight: '500', 
                        cursor: 'pointer', 
                        transition: 'all 0.3s ease',
                        ':hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                        },
                        ...(getRemainingImagesCount() < 10 ? { 
                          opacity: 0.5, 
                          cursor: 'not-allowed',
                          ':hover': {
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        } : {})
                      }}
                      disabled={getRemainingImagesCount() < 10}
                    >
                      <FiCheck /> {editingProject ? 'Update Project' : 'Add Project'}
                      {getRemainingImagesCount() < 10 && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '12px', 
                          opacity: '0.8' 
                        }}>
                          (Need {10 - getRemainingImagesCount()} more images)
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default ProjectManagementDataTable;